import { Collapsible } from '@/components/Collapsible';
import CustomCard from '@/components/customCard';
import { RuleSummaryStyled } from '@/components/RuleSummary/styled';
import { DisplayModeEnum } from '@/constants/enum';
import { MAP_RULE_DISPLAY_TIME_LABEL } from '@/constants/label';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { Badge, Button, List } from '@shopify/polaris';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

interface IProps {
  defaultData: any;
  isDisableActive?: boolean;
  handleStatus: () => void;
  isEmptyCondition: boolean;
  locationConditions: string[];
  itemConditions: string[];
  textDataException: React.ReactNode;
  hasDataAdvance?: boolean;
  isInCheckout?: boolean;
}

const RuleSummary = ({
  defaultData,
  isDisableActive,
  handleStatus,
  isEmptyCondition,
  locationConditions,
  itemConditions,
  textDataException,
  hasDataAdvance,
  isInCheckout,
}: IProps) => {
  const dataSettings = useSelector(dataSettingsSelector);

  const currentItemConditions = useMemo(() => {
    if (isInCheckout) {
      return [...itemConditions, ...locationConditions];
    }
    return itemConditions;
  }, [isInCheckout, itemConditions, locationConditions]);

  return (
    <CustomCard
      title={<span style={{ fontSize: '15px' }}>Rule summary</span>}
      style={{ marginBottom: '0.75rem', borderRadius: '16px' }}
    >
      <RuleSummaryStyled>
        <div className="d-flex gap-12 flex-column">
          <div className="d-flex justify-between w-full align-center mt-8">
            <div>
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Rule status </span>{' '}
              <Badge tone={defaultData?.enable ? 'success' : undefined}>{defaultData?.enable ? 'Active' : 'Inactive'}</Badge>
            </div>
            <Button onClick={handleStatus} disabled={isDisableActive}>
              {defaultData?.enable ? 'Deactivate' : 'Activate'}
            </Button>
          </div>

          <Collapsible padding={'0'} title="Display condition">
            {isEmptyCondition ? (
              <span>Show on all products</span>
            ) : (
              <>
                {!isInCheckout && locationConditions?.length > 0 && (
                  <div className="d-flex flex-column gap-4">
                    <span>For visitor from</span>
                    <List>
                      {locationConditions?.map((item, index) => (
                        <List.Item key={index}>{item}</List.Item>
                      ))}
                    </List>
                  </div>
                )}
                {currentItemConditions?.length > 0 && (
                  <div className="d-flex flex-column gap-4">
                    <span className={!isInCheckout && locationConditions?.length > 0 ? 'mt-16' : ''}>Show if:</span>
                    <List>
                      {currentItemConditions?.map((item, index) => (
                        <React.Fragment key={index}>
                          <List.Item>{item}</List.Item>
                          {index !== currentItemConditions.length - 1 && currentItemConditions.length > 1 && (
                            <span style={{ marginLeft: '-20px' }}>
                              {defaultData?.typeRelationOfItemCondition?.toLocaleUpperCase()}
                            </span>
                          )}
                        </React.Fragment>
                      ))}
                    </List>
                    {textDataException ? textDataException : null}
                  </div>
                )}
              </>
            )}
            {defaultData.displayMode && defaultData.displayMode !== DisplayModeEnum.AllTime ? (
              <div className="mt-8">
                Rule display time: {MAP_RULE_DISPLAY_TIME_LABEL[defaultData.displayMode as DisplayModeEnum]}
              </div>
            ) : null}
          </Collapsible>
          {isInCheckout ? null : (
            <Collapsible padding={'0'} title="Advance setup">
              {hasDataAdvance ? (
                <List>
                  {defaultData?.showTextInCart && (
                    <List.Item>
                      {dataSettings?.isDynamicDateInCart ? 'Show dynamic EDD in Cart' : 'Show static EDD in Cart'}
                    </List.Item>
                  )}
                  {defaultData?.soldOutEnabled && <List.Item>Show when out of stock</List.Item>}
                </List>
              ) : (
                'None'
              )}
            </Collapsible>
          )}
        </div>
      </RuleSummaryStyled>
    </CustomCard>
  );
};

export default RuleSummary;
